/**
 * Remember which Firestore run ids have had full telemetry loaded on this browser
 * (so list cards can show "cached on device" vs "cloud only").
 */

const STORAGE_KEY = 'shokiq_run_device_cache_v1';
const MAX_IDS_PER_USER = 400;

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const o = raw ? JSON.parse(raw) : {};
    return typeof o === 'object' && o !== null ? o : {};
  } catch {
    return {};
  }
}

function writeStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (e) {
    console.warn('runDeviceCache write failed:', e);
  }
}

function dispatchMarked(userId, runId) {
  try {
    window.dispatchEvent(new CustomEvent('shokiq-run-device-cache', { detail: { userId, runId } }));
  } catch {
    // ignore
  }
}

/**
 * Mark a cloud run as having telemetry available locally in this session/browser.
 * No-op for pending queue ids (local-*, queued-inline-*).
 */
export function markRunTelemetryCachedOnDevice(userId, runId) {
  if (!userId || !runId || typeof runId !== 'string') return;
  if (runId.startsWith('local-') || runId.startsWith('queued-inline-')) return;

  const store = readStore();
  const prev = store[userId] && typeof store[userId] === 'object' ? store[userId] : {};
  const bucket = { ...prev, [runId]: Date.now() };
  const ids = Object.keys(bucket);
  if (ids.length > MAX_IDS_PER_USER) {
    ids.sort((a, b) => (bucket[a] || 0) - (bucket[b] || 0));
    const overflow = ids.length - MAX_IDS_PER_USER;
    for (let i = 0; i < overflow; i += 1) {
      delete bucket[ids[i]];
    }
  }
  store[userId] = bucket;
  writeStore(store);
  dispatchMarked(userId, runId);
}

/** Undo laptop icon when a run no longer has retrievable telemetry on this device (e.g. bad Storage URL). */
export function clearRunTelemetryCachedOnDevice(userId, runId) {
  if (!userId || !runId || typeof runId !== 'string') return;
  if (runId.startsWith('local-') || runId.startsWith('queued-inline-')) return;
  const store = readStore();
  const prev = store[userId] && typeof store[userId] === 'object' ? store[userId] : {};
  if (!prev[runId]) return;
  const bucket = { ...prev };
  delete bucket[runId];
  store[userId] = bucket;
  writeStore(store);
  dispatchMarked(userId, runId);
}

export function isRunTelemetryCachedOnDevice(userId, runId) {
  if (!userId || !runId || typeof runId !== 'string') return false;
  if (runId.startsWith('local-') || runId.startsWith('queued-inline-')) return false;
  const store = readStore();
  return Boolean(store[userId]?.[runId]);
}
